import { css, StyleSheet } from 'aphrodite';
import React from 'react';
import { FormControl, IconButton, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Tooltip from '@material-ui/core/Tooltip';

import { ApiHelper } from '../../../common/helpers/ApiHelper';
import { Helper } from '../../../common/helpers/Helper';
import { UiHelper } from '../../../common/helpers/UiHelper';
import { BaseEntityPageSlixta } from '../../../components/BaseEntityPageSlixta';
import { AppConfig } from '../../../AppConfig';
import { AppStyles, AppTheme } from '../../../styles/AppTheme';
import { CommonTheme } from '../../../styles/Styles';
import { PageDialog } from '../../../common/components/PageDialog';
import { SearchBar } from '../../../common/components/SearchBar';
import WhatsappTemplatePreview from '../../../common/components/WhatsappTemplatePreview';
import WhatsappTemplateAddForm from '../../../common/components/WhatsappTemplateAddForm';
import { WhatsappUtils, buildWaNumberLabel, buildWaNumberSelectOptions, extractCrmNumbersItems, getWaOrgAccountNumbers, withVendorProjectIdQueryParam } from './WhatsappUtils';
import { plainTextToWaBodyRichText, whatsAppBodyTextToWaBodyRichText } from './WhatsappTemplateDetail';
import BulkCopyTemplateForm from '../../../common/components/BulkCopyTemplateForm';
import { Controller } from '../../../common/Controller';
import { CdpHelper } from '../../../helpers/CdpHelper';

function normalizeTemplatesResponse(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  if (Array.isArray(data.templates)) return data.templates
  if (Array.isArray(data.data)) return data.data
  return []
}

function templateDisplayName(t) {
  return String(t.name || t.templateName || t.title || '').trim()
}

function templateCategory(t) {
  return String(t.category || '').trim()
}

function templateType(t) {
  if (!t || typeof t !== 'object') return 'TEXT'

  const components = Array.isArray(t.components) ? t.components : []
  const headerComp = components.find(c => String(c.type || '').toUpperCase() === 'HEADER')
  if (!headerComp) return 'TEXT'
  const fmt = String(headerComp.format || '').trim().toUpperCase()
  if (['IMAGE', 'VIDEO', 'DOCUMENT', 'TEXT'].includes(fmt)) return fmt
  return 'TEXT'
}

function statusBadgeStyle(status) {
  const s = String(status || '').toUpperCase()
  if (['APPROVED', 'ACTIVE', 'LIVE'].includes(s)) return Styles.statusBadgeSuccess
  if (['PENDING', 'IN_REVIEW'].includes(s)) return Styles.statusBadgePending
  if (['REJECTED', 'FAILED', 'DISABLED'].includes(s)) return Styles.statusBadgeError
  return Styles.statusBadgeDefault
}

const CATEGORY_FILTER_ALL = { key: 'all', label: 'All' }
const TYPE_FILTER_ALL = { key: 'all', label: 'All' }
const STATUS_FILTER_ALL = { key: 'all', label: 'All' }
const CATEGORY_STATIC_OPTIONS = ['MARKETING', 'UTILITY']
const TYPE_STATIC_OPTIONS = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT']
const STATUS_STATIC_OPTIONS = ['PENDING', 'APPROVED']

function buildFilterOptions(allOption, staticValues, dynamicValues) {
  const out = [allOption]
  const seen = new Set()
  ;(staticValues || []).forEach(v => {
    const t = String(v || '').trim()
    if (!t) return
    out.push({ key: t, label: t })
    seen.add(t.toLowerCase())
  })
  ;(dynamicValues || []).forEach(v => {
    const t = String(v || '').trim()
    if (!t) return
    const k = t.toLowerCase()
    if (seen.has(k)) return
    out.push({ key: t, label: t })
    seen.add(k)
  })
  return out
}

function filterTemplatesRows(masterList, wabaSelected, categorySelected, typeSelected, statusSelected, searchText) {
  let rows = masterList || []
  if (wabaSelected && wabaSelected.key) {
    rows = rows.filter(r => r.wabaNumber === wabaSelected.key)
  }
  if (categorySelected && categorySelected.key && categorySelected.key !== 'all') {
    rows = rows.filter(r => String(r.category || '').toLowerCase() === String(categorySelected.key).toLowerCase())
  }
  if (typeSelected && typeSelected.key && typeSelected.key !== 'all') {
    rows = rows.filter(r => String(r.type || '').toLowerCase() === String(typeSelected.key).toLowerCase())
  }
  if (statusSelected && statusSelected.key && statusSelected.key !== 'all') {
    rows = rows.filter(r => String(r.status || '').toLowerCase() === String(statusSelected.key).toLowerCase())
  }
  const q = (searchText || '').trim().toLowerCase()
  if (q) {
    rows = rows.filter(r => String(r.name || '').toLowerCase().includes(q))
  }
  return rows
}

function normalizePreviewComponents(components) {
  const list = Array.isArray(components) ? components : []
  return list.map(comp => {
    if (!comp || String(comp.type || '').toUpperCase() !== 'BODY') return comp
    if (comp.text && String(comp.text).trim()) return comp
    if (!comp.bodyRichText) return comp
    const text = WhatsappUtils.waRichTextToWhatsAppBodyText(comp.bodyRichText)
    return text ? { ...comp, text } : comp
  })
}

function normalizeKeyList(value) {
  if (!Array.isArray(value)) {
    if (typeof value === 'string' && value.trim()) {
      return value.split(',').map(s => s.trim()).filter(Boolean)
    }
    return []
  }
  const out = []
  const seen = new Set()
  value.forEach(v => {
    let t = ''
    if (v && typeof v === 'object' && v.key != null) {
      t = String(v.key).trim()
    } else {
      t = String(v || '').trim()
    }
    if (!t || seen.has(t)) return
    seen.add(t)
    out.push(t)
  })
  return out
}
const normalizeTagList = normalizeKeyList

function normalizeKeyAssignments(assignments) {
  if (!assignments || typeof assignments !== 'object' || Array.isArray(assignments)) return {}
  const out = {}
  Object.keys(assignments).forEach(templateName => {
    const name = String(templateName || '').trim()
    if (!name) return
    out[name] = normalizeKeyList(assignments[templateName])
  })
  return out
}
const normalizeTagAssignments = normalizeKeyAssignments

function keysEqual(a, b) {
  const aa = [...normalizeKeyList(a)].sort()
  const bb = [...normalizeKeyList(b)].sort()
  if (aa.length !== bb.length) return false
  return aa.every((t, i) => t === bb[i])
}
const tagsEqual = keysEqual

function unusedKeys(availableKeys, assignments) {
  const used = new Set()
  Object.keys(assignments || {}).forEach(name => {
    ;(assignments[name] || []).forEach(k => used.add(k))
  })
  return (availableKeys || []).filter(k => !used.has(k))
}
const unusedTags = unusedKeys

function emptyKeyState() {
  return {
    availableKeys: [],
    availableTags: [],
    keyAssignments: {},
    tagAssignments: {},
    templateKeysLoading: false,
    tagTagsLoading: false,
  }
}
const emptyTagState = emptyKeyState

function buildKeyCheckBoxOptions(availableKeys) {
  return (availableKeys || []).map(k => ({
    key: k,
    label: k,
  }))
}
const buildTagCheckBoxOptions = buildKeyCheckBoxOptions

function extractAvailableKeys(data) {
  if (!data) return []
  if (Array.isArray(data.availableKeys)) return normalizeKeyList(data.availableKeys)
  if (Array.isArray(data.availableTags)) return normalizeKeyList(data.availableTags)
  return []
}

export class WhatsappTemplates extends BaseEntityPageSlixta {
  constructor(props) {
    super(props);
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = 'WhatsApp templates'
    this.pageKey = 'whatsapp-templates'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.noAdd = true
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      {
        key: 'waAccounts',
        ms: this.ms,
        method: 'GET',
        paths: ['miscs', 'get-wa-accounts'],
        queryParams: { orgId: this.orgId },
      },
      {
        key: 'crmnumbers',
        ms: this.ms,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId: this.orgId },
      },
    ]
    this.apiData = {
      orgId: this.orgId,
    }
    this.tableHeadersMap = {
      Index: current => current.__index + 1,
      Name: current => this.renderTemplateNameCell(current),
      'WABA number': current => (
        <div>
          <span className={css(Styles.line2)}>{buildWaNumberLabel(current.wabaNumber, this.waNumberDetails) || '—'}</span>
        </div>
      ),
      Category: current => (
        <div>
          <span className={css(Styles.line2)}>{current.category || '—'}</span>
        </div>
      ),
      Type: current => (
        <div>
          <span className={css(Styles.line2)}>{current.type || 'TEXT'}</span>
        </div>
      ),
      Status: current => (
        <div>
          <span className={css(Styles.statusBadge, statusBadgeStyle(current.status))}>
            {current.status || '—'}
          </span>
        </div>
      ),
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = [
      {
        label: 'Add Template',
        onClick: () => this.openModalForAddTemplate(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => this.fetchItems(),
      },
    ]
    this.state = {
      ...this.state,
      masterTemplateList: [],
      wabaFilterSelected: null,
      wabaOptions: [],
      categoryFilterSelected: CATEGORY_FILTER_ALL,
      categoryOptions: [CATEGORY_FILTER_ALL],
      typeFilterSelected: TYPE_FILTER_ALL,
      typeOptions: [TYPE_FILTER_ALL],
      statusFilterSelected: STATUS_FILTER_ALL,
      statusOptions: [STATUS_FILTER_ALL],
      searchText: '',
      waFromNumbers: [],
      waNumberDetails: undefined,
      templatesByWaba: {},
      previewTemplateComponents: [],
      isTemplatePreviewDialogOpen: false,
      templatePreviewTitle: '',
      wabaTemplatesLoading: false,
      isAddTemplateDialogOpen: false,
      addTemplateInitialForm: null,
      isBulkCopyDialogOpen: false,
      bulkCopyTemplate: null,
      bulkCopySelectedWabas: [],
      bulkCopyAvailableWabas: [],
      bulkCopyInProgress: false,
      availableKeys: [],
      availableTags: [],
      keyAssignments: {},
      tagAssignments: {},
      templateKeysLoading: false,
      tagTagsLoading: false,
      deletingKey: null,
    }
  }

  fetchItems(onReadyCallback) {
    const prev = this._waTemplatesFetchCount || 0
    this._waTemplatesFetchCount = prev + 1
    if (prev > 0) {
      this.setState({ loadingMode: ApiHelper.LOADING_MODE.INSTANT_LOAD })
    }
    super.fetchItems(onReadyCallback)
  }

  canEditTemplateKeys() {
    const roleHelper = Controller.get().roleHelper()
    return !roleHelper.hasCurrentOrgRole('VIEWER')
  }

  canEditTemplateTags() {
    return this.canEditTemplateKeys()
  }

  nextKeyFetchSeq() {
    this._keyFetchSeq = (this._keyFetchSeq || 0) + 1
    return this._keyFetchSeq
  }

  nextTagFetchSeq() {
    return this.nextKeyFetchSeq()
  }

  applyKeyState(keyData, extras = {}) {
    const availableKeys = extractAvailableKeys(keyData)
    const keyAssignments = normalizeKeyAssignments(keyData && (keyData.assignments || keyData.keyAssignments))
    this.setState({
      ...extras,
      availableKeys,
      availableTags: availableKeys,
      keyAssignments,
      tagAssignments: keyAssignments,
      templateKeysLoading: false,
      tagTagsLoading: false,
    })
  }

  applyTagState(tagData, extras = {}) {
    this.applyKeyState(tagData, extras)
  }

  fetchTemplateKeys = async (fromNumber) => {
    if (!fromNumber) {
      return { availableKeys: [], assignments: {}, fromNumber: '' }
    }
    const data = await ApiHelper.callAwait({
      ms: this.ms,
      method: 'GET',
      paths: ['miscs', 'get-wa-template-tags'],
      queryParams: { orgId: this.orgId, fromNumber },
    })
    return {
      fromNumber: (data && data.fromNumber) || fromNumber,
      availableKeys: extractAvailableKeys(data),
      assignments: normalizeKeyAssignments(data && data.assignments),
    }
  }

  fetchTemplateTags = this.fetchTemplateKeys

  loadKeysForWaba = async (fromNumber, extras = {}) => {
    const seq = this.nextKeyFetchSeq()
    if (!fromNumber) {
      this.applyKeyState(emptyKeyState(), extras)
      return
    }
    try {
      const keyData = await this.fetchTemplateKeys(fromNumber)
      if (seq !== this._keyFetchSeq) return
      this.applyKeyState(keyData, extras)
    } catch (err) {
      if (seq !== this._keyFetchSeq) return
      this.applyKeyState(emptyKeyState(), extras)
      this.showMessage(Helper.getErrorMsg(err), undefined, 'Error')
    }
  }

  loadTagsForWaba = this.loadKeysForWaba

  renderTemplateNameCell(current) {
    const availableKeys = this.state.availableKeys || []
    const keys = availableKeys.length
      ? ((this.state.keyAssignments && this.state.keyAssignments[current.name])
        || (this.state.tagAssignments && this.state.tagAssignments[current.name])
        || [])
      : []
    return (
      <div className={css(Styles.nameCell)}>
        <span className={css(Styles.line1, Styles.nameTitle)}>{current.name || '—'}</span>
        {keys.map(key => (
          <span key={key} className={css(Styles.keyChip, Styles.tagChip)}>{key}</span>
        ))}
      </div>
    )
  }

  openKeyDialog = (current) => {
    if (!this.canEditTemplateKeys()) return
    const availableKeys = this.state.availableKeys || []
    const templateName = current.name
    this._keyEditTemplateName = templateName
    this._tagEditTemplateName = templateName
    const assigned = (this.state.keyAssignments && this.state.keyAssignments[templateName])
      || (this.state.tagAssignments && this.state.tagAssignments[templateName])
      || []
    this.formDefinition = {
      title: `Keys — ${templateName || '—'}`,
      formType: 'edit',
      ctaButtonText: 'Save',
      maxWidth: 'sm',
      description: 'Each key can be on only one template for this WABA number. Saving a key here removes it from any other template on this number.',
      steps: [
        {
          fields: [
            {
              key: 'keys',
              type: 'check_box',
              label: 'Keys',
              placement: 'left',
              fullWidth: true,
              addOptions: true,
              options: buildKeyCheckBoxOptions(availableKeys),
              v_required: {
                value: false,
                err: '',
              },
            },
          ],
        },
      ],
    }
    this.openModalForEdit({ keys: assigned, tags: assigned })
  }

  openTagDialog = this.openKeyDialog

  closeKeyDialog = () => {
    this._keyEditTemplateName = null
    this._tagEditTemplateName = null
    this.setState({ modalOpen: false })
  }

  closeTagDialog = this.closeKeyDialog

  onFormFieldOptionAdd = async ({ formField, value }) => {
    if (!formField) return []
    if (!formField.options) formField.options = []
    const trimmed = String(value || '').trim()
    if (!trimmed) return formField.options
    if (!formField.options.some(o => o.key === trimmed || o.label === trimmed)) {
      formField.options.push({ key: trimmed, label: trimmed, text: trimmed })
    }
    return formField.options
  }

  appendKeyToSettings = async (newKeys) => {
    const prevSetting = CdpHelper.getSetting ? CdpHelper.getSetting('WA_TEMPLATE_TAGS', this.orgId) : null
    const prevVal = typeof prevSetting === 'string' && prevSetting.length > 0
      ? prevSetting
      : (this.state.availableKeys || []).join(',')

    const currentKeys = prevVal
      ? prevVal.split(',').map(s => s.trim()).filter(Boolean)
      : [...(this.state.availableKeys || [])]

    ;(newKeys || []).forEach(k => {
      const trimmed = String(k || '').trim()
      if (trimmed && !currentKeys.includes(trimmed)) {
        currentKeys.push(trimmed)
      }
    })
    const updatedSettingsValue = currentKeys.join(',')

    await ApiHelper.callAwait({
      ms: this.ms,
      method: 'POST',
      paths: ['miscs', 'update-cdp-settings'],
      queryParams: { orgId: this.orgId },
      jsonBody: {
        orgId: this.orgId,
        settingsKey: 'WA_TEMPLATE_TAGS',
        settingsValue: updatedSettingsValue,
        previousKey: 'WA_TEMPLATE_TAGS',
        previousValue: prevVal || '',
      },
    })

    if (CdpHelper._cdpSettings && CdpHelper._cdpSettings[this.orgId]) {
      CdpHelper._cdpSettings[this.orgId].WA_TEMPLATE_TAGS = updatedSettingsValue
    }
  }

  deleteKeyFromSettings = async (keyToDelete) => {
    if (!this.canEditTemplateKeys()) return
    const target = String(keyToDelete || '').trim()
    if (!target) return
    if (this._deletingKeyLock) return
    this._deletingKeyLock = true
    this.setState({ deletingKey: target })
    try {
      const prevSetting = CdpHelper.getSetting ? CdpHelper.getSetting('WA_TEMPLATE_TAGS', this.orgId) : null
      const prevVal = typeof prevSetting === 'string' && prevSetting.length > 0
        ? prevSetting
        : (this.state.availableKeys || []).join(',')

      const rawList = prevVal
        ? prevVal.split(',').map(s => s.trim()).filter(Boolean)
        : [...(this.state.availableKeys || [])]

      const seen = new Set()
      const allCurrent = []
      ;[...rawList, ...(this.state.availableKeys || [])].forEach(k => {
        const trimmed = String(k || '').trim()
        if (trimmed && !seen.has(trimmed)) {
          seen.add(trimmed)
          allCurrent.push(trimmed)
        }
      })

      const updatedKeys = allCurrent.filter(k => k !== target)
      const updatedSettingsValue = updatedKeys.join(',')

      await ApiHelper.callAwait({
        ms: this.ms,
        method: 'POST',
        paths: ['miscs', 'update-cdp-settings'],
        queryParams: { orgId: this.orgId },
        jsonBody: {
          orgId: this.orgId,
          settingsKey: 'WA_TEMPLATE_TAGS',
          settingsValue: updatedSettingsValue,
          previousKey: 'WA_TEMPLATE_TAGS',
          previousValue: prevVal || '',
        },
      })

      if (CdpHelper._cdpSettings && CdpHelper._cdpSettings[this.orgId]) {
        CdpHelper._cdpSettings[this.orgId].WA_TEMPLATE_TAGS = updatedSettingsValue
      }

      this.setState({
        availableKeys: updatedKeys,
        availableTags: updatedKeys,
        deletingKey: null,
      })
    } catch (err) {
      this.setState({ deletingKey: null })
      this.showMessage(Helper.getErrorMsg(err), undefined, 'Error')
    } finally {
      this._deletingKeyLock = false
    }
  }

  deleteTagFromSettings = this.deleteKeyFromSettings
  deleteKey = this.deleteKeyFromSettings

  customSubmit = async (options, done) => {
    const { formData, caller } = options
    const templateName = this._keyEditTemplateName || this._tagEditTemplateName
    if (!templateName) {
      if (done) done()
      return
    }
    try {
      const keys = formData && (formData.keys !== undefined ? formData.keys : formData.tags)
      await this.saveTemplateKeys(templateName, keys)
      this._keyEditTemplateName = null
      this._tagEditTemplateName = null
      if (done) done()
    } catch (err) {
      caller.setState({
        success: false,
        message: Helper.getErrorMsg(err),
        inProcess: false,
      })
    }
  }

  saveTemplateKeys = async (templateName, keys) => {
    if (!this.canEditTemplateKeys()) return
    const fromNumber = this.state.wabaFilterSelected && this.state.wabaFilterSelected.key
    if (!fromNumber || !templateName) return
    const available = this.state.availableKeys || []
    const selected = normalizeKeyList(keys)
    const newKeys = selected.filter(k => !available.includes(k))
    const current = (this.state.keyAssignments && this.state.keyAssignments[templateName])
      || (this.state.tagAssignments && this.state.tagAssignments[templateName])
      || []
    if (newKeys.length === 0 && keysEqual(current, selected)) return
    if (this._savingKeysLock) return
    this._savingKeysLock = true
    try {
      if (newKeys.length > 0) {
        // Step 1: Call POST /miscs/update-cdp-settings to append the new tag to WA_TEMPLATE_TAGS
        await this.appendKeyToSettings(newKeys)
      }
      // Step 2: Call POST /miscs/update-wa-template-tags with the template name and updated tags array (which now includes the new tag).
      // This call immediately responds with the updated availableTags, availableKeys, and assignments.
      const data = await ApiHelper.callAwait({
        ms: this.ms,
        method: 'POST',
        paths: ['miscs', 'update-wa-template-tags'],
        queryParams: { orgId: this.orgId },
        jsonBody: {
          orgId: this.orgId,
          fromNumber,
          template: templateName,
          tags: selected,
        },
      })
      const returnedKeys = extractAvailableKeys(data)
      this.applyKeyState({
        availableKeys: returnedKeys.length ? returnedKeys : [...available, ...newKeys],
        assignments: data && data.assignments,
      })
    } finally {
      this._savingKeysLock = false
    }
  }

  saveTemplateTags = this.saveTemplateKeys

  buildRowsForWaba = async (fromNumber) => {
    if (!fromNumber) return []
    const data = await ApiHelper.callAwait({
      ms: this.ms,
      method: 'GET',
      paths: ['hooks'],
      queryParams: withVendorProjectIdQueryParam({
        orgId: this.orgId,
        fromNumber,
        bridgePath: 'whatsapptemplates',
      }, fromNumber, this.crmNumbers),
    })
    const list = normalizeTemplatesResponse(data)
    const rows = list.map((t, idx) => {
      const name = templateDisplayName(t)
      const status = t.status || t.templateStatus || t.state || ''
      const category = templateCategory(t)
      const type = templateType(t)
      const id = `${fromNumber}_${name || 't'}_${idx}`
      return {
        id,
        name,
        status,
        category,
        type,
        components: Array.isArray(t.components) ? t.components : [],
        wabaNumber: fromNumber,
      }
    })
    return rows
  }

  applyRowsForSelectedWaba = (rows, selectedWaba, extras = {}) => {
    const categoryValues = [...new Set(rows.map(r => String(r.category || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    const typeValues = [...new Set(rows.map(r => String(r.type || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    const statusValues = [...new Set(rows.map(r => String(r.status || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    const categoryOptions = buildFilterOptions(CATEGORY_FILTER_ALL, CATEGORY_STATIC_OPTIONS, categoryValues)
    const typeOptions = buildFilterOptions(TYPE_FILTER_ALL, TYPE_STATIC_OPTIONS, typeValues)
    const statusOptions = buildFilterOptions(STATUS_FILTER_ALL, STATUS_STATIC_OPTIONS, statusValues)
    const filteredRows = filterTemplatesRows(
      rows,
      selectedWaba,
      CATEGORY_FILTER_ALL,
      TYPE_FILTER_ALL,
      STATUS_FILTER_ALL,
      ''
    )
    this.setState({
      ...extras,
      masterTemplateList: rows,
      wabaFilterSelected: selectedWaba,
      categoryFilterSelected: CATEGORY_FILTER_ALL,
      typeFilterSelected: TYPE_FILTER_ALL,
      statusFilterSelected: STATUS_FILTER_ALL,
      categoryOptions,
      typeOptions,
      statusOptions,
      searchText: '',
      items: filteredRows,
    })
  }

  async onFetchSuccess(results) {
    const waAccounts = this.getResult(results, 'waAccounts')
    const waOrgAccounts = waAccounts && waAccounts[this.orgId]
    const { fromNumbers, waNumberDetails } = getWaOrgAccountNumbers(waOrgAccounts)
    this.waNumberDetails = waNumberDetails
    this.crmNumbers = extractCrmNumbersItems(this.getResult(results, 'crmnumbers'))
    const wabaOptions = buildWaNumberSelectOptions(fromNumbers, waNumberDetails)
    const firstWaba = wabaOptions[0] || null
    const currentSelectedKey = this.state.wabaFilterSelected && this.state.wabaFilterSelected.key
    const selectedWaba = wabaOptions.find(w => w.key === currentSelectedKey) || firstWaba
    if (!selectedWaba) {
      this.setState({
        masterTemplateList: [],
        wabaFilterSelected: null,
        categoryFilterSelected: CATEGORY_FILTER_ALL,
        typeFilterSelected: TYPE_FILTER_ALL,
        statusFilterSelected: STATUS_FILTER_ALL,
        wabaOptions: [],
        categoryOptions: [CATEGORY_FILTER_ALL],
        typeOptions: [TYPE_FILTER_ALL],
        statusOptions: [STATUS_FILTER_ALL],
        searchText: '',
        items: [],
        waFromNumbers: fromNumbers,
        waNumberDetails,
        templatesByWaba: {},
        ...emptyKeyState(),
        modalOpen: false,
      })
      return
    }
    try {
      const seq = this.nextKeyFetchSeq()
      this.setState({ templateKeysLoading: true, tagTagsLoading: true })
      const [rows, keyData] = await Promise.all([
        this.buildRowsForWaba(selectedWaba.key),
        this.fetchTemplateKeys(selectedWaba.key).catch(err => {
          this.showMessage(Helper.getErrorMsg(err), undefined, 'Error')
          return { availableKeys: [], assignments: {}, fromNumber: selectedWaba.key }
        }),
      ])
      if (seq !== this._keyFetchSeq) return
      const availableKeys = extractAvailableKeys(keyData)
      const keyAssignments = normalizeKeyAssignments(keyData && keyData.assignments)
      this.applyRowsForSelectedWaba(rows, selectedWaba, {
        wabaOptions,
        waFromNumbers: fromNumbers,
        waNumberDetails,
        templatesByWaba: { [selectedWaba.key]: rows },
        availableKeys,
        availableTags: availableKeys,
        keyAssignments,
        tagAssignments: keyAssignments,
        templateKeysLoading: false,
        tagTagsLoading: false,
      })
    } catch (e) {
      this.applyRowsForSelectedWaba([], selectedWaba, {
        wabaOptions,
        waFromNumbers: fromNumbers,
        waNumberDetails,
        templatesByWaba: {},
        ...emptyKeyState(),
      })
    }
  }

  openModalForAddTemplate = () => {
    this.setState({ isAddTemplateDialogOpen: true, addTemplateInitialForm: null, })
  }

  openCopyTemplateDialog = (current) => {
    this.setState({
      addTemplateInitialForm: this.buildCopyTemplateInitialForm(current),
      isAddTemplateDialogOpen: true,
    })
  }

  buildCopyTemplateInitialForm = (template) => {
    const components = Array.isArray(template.components) ? template.components : []

    const header = components.find(c => String(c.type || '').toUpperCase() === 'HEADER')
    const body = components.find(c => String(c.type || '').toUpperCase() === 'BODY')
    const footer = components.find(c => String(c.type || '').toUpperCase() === 'FOOTER')
    const buttonsComp = components.find(c => String(c.type || '').toUpperCase() === 'BUTTONS')

    const headerType = header ? String(header.format || 'TEXT').toUpperCase() : 'NONE'
    const bodyText = body && body.text ? String(body.text) : ''

    const bodyVariableExamplesByToken = {}
    const bodyExamples = body && body.example && Array.isArray(body.example.body_text) && Array.isArray(body.example.body_text[0])
        ? body.example.body_text[0] : []

    ;(bodyText.match(/\{\{\d+\}\}/g) || []).forEach(token => {
      const idx = parseInt(token.replace(/[{}]/g, ''), 10)
      if (!Number.isNaN(idx)) {
        bodyVariableExamplesByToken[token] = bodyExamples[idx - 1] || ''
      }
    })

    const buttonRows = ((buttonsComp && buttonsComp.buttons) || []).slice(0, 3).map((button, idx) => {
    const type = String(button.type || '').toUpperCase()

      return {
        id: `btn_copy_${Date.now()}_${idx}`,
        type: type === 'PHONE_NUMBER' ? 'PHONE' : type,
        text: button.text || '',
        url: type === 'URL' ? button.url || '' : '',
        phone: type === 'PHONE_NUMBER' ? button.phone_number || '' : '',
        urlVariable: type === 'URL' && Array.isArray(button.example) ? button.example[0] || '' : '',
        flowId: type === 'FLOW' ? String(button.flow_id || '') : '',
      }
    })
    
    return {
      wabaNumber: template.wabaNumber || '',
      title: template.name ? `${template.name}_copy` : '',
      iType: template.category || 'UTILITY',
      headerType,
      headerText: headerType === 'TEXT' ? header.text || '' : '',
      headerTextVariableExample:
        header &&
        header.example &&
        Array.isArray(header.example.header_text)
          ? header.example.header_text[0] || ''
          : '',
      bodyRichText: body && body.bodyRichText ? body.bodyRichText : whatsAppBodyTextToWaBodyRichText(bodyText),
      bodyVariableExamplesByToken,
      footerText: footer ? footer.text || '' : '',
      buttonRows,
    }
  }

  openBulkCopyTemplateDialog = (current) => {
    // Get list of WABA numbers excluding the current one
    const availableWabaNumbers = (this.state.wabaOptions || []).filter(
      waba => waba.key !== current.wabaNumber
    )
  
    if (availableWabaNumbers.length === 0) {
      this.showMessage('No other WABA numbers available for bulk copy', undefined, 'Info')
      return
    }
  
    this.setState({
      isBulkCopyDialogOpen: true,
      bulkCopyTemplate: current,
      bulkCopySelectedWabas: [],
      bulkCopyAvailableWabas: availableWabaNumbers,
      bulkCopyInProgress: false,
    })
  }
  
  closeBulkCopyDialog = () => {
    this.setState({
      isBulkCopyDialogOpen: false,
      bulkCopyTemplate: null,
      bulkCopySelectedWabas: [],
      bulkCopyAvailableWabas: [],
      bulkCopyInProgress: false,
    })
  }
  
  onBulkCopyWabaSelectionChange = (event, selectedWabas) => {
    this.setState({
      bulkCopySelectedWabas: selectedWabas || [],
    })
  }
  
  onBulkCopySubmit = ({ successCount, failedNumbers, selectedWabaKeys }) => {
    this.setState({
      isBulkCopyDialogOpen: false,
      bulkCopyInProgress: false,
    })
  
    if (failedNumbers.length === 0) {
      this.showMessage(
        `Template copied to ${successCount} WABA number(s) successfully`,
        undefined,
        'Success'
      )
    } else if (successCount > 0) {
      this.showMessage(
        `Template copied to ${successCount} WABA(s). Failed for: ${failedNumbers.join(', ')}`,
        undefined,
        'Warning'
      )
    } else {
      this.showMessage(
        'Failed to copy template to all WABA numbers',
        undefined,
        'Error'
      )
    }
  
    this.fetchItems()
  }

  onWabaFilterChange = async (e, val) => {
    if (!val || !val.key) return
    if (this.state.wabaFilterSelected && this.state.wabaFilterSelected.key === val.key) return
    this.closeKeyDialog()
    const cachedRows = this.state.templatesByWaba && this.state.templatesByWaba[val.key]
    if (cachedRows) {
      this.applyRowsForSelectedWaba(cachedRows, val, {
        keyAssignments: {},
        tagAssignments: {},
        templateKeysLoading: true,
        tagTagsLoading: true,
      })
      await this.loadKeysForWaba(val.key)
      return
    }
    try {
      this.setState({
        wabaTemplatesLoading: true,
        templateKeysLoading: true,
        tagTagsLoading: true,
        keyAssignments: {},
        tagAssignments: {},
      })
      const seq = this.nextKeyFetchSeq()
      const [rows, keyData] = await Promise.all([
        this.buildRowsForWaba(val.key),
        this.fetchTemplateKeys(val.key).catch(err => {
          this.showMessage(Helper.getErrorMsg(err), undefined, 'Error')
          return { availableKeys: [], assignments: {}, fromNumber: val.key }
        }),
      ])
      if (seq !== this._keyFetchSeq) return
      const availableKeys = extractAvailableKeys(keyData)
      const keyAssignments = normalizeKeyAssignments(keyData && keyData.assignments)
      this.applyRowsForSelectedWaba(rows, val, {
        templatesByWaba: {
          ...(this.state.templatesByWaba || {}),
          [val.key]: rows,
        },
        wabaTemplatesLoading: false,
        availableKeys,
        availableTags: availableKeys,
        keyAssignments,
        tagAssignments: keyAssignments,
        templateKeysLoading: false,
        tagTagsLoading: false,
      })
    } catch (err) {
      this.setState({ wabaTemplatesLoading: false, templateKeysLoading: false, tagTagsLoading: false })
      this.showMessage(Helper.getErrorMsg(err), undefined, 'Error')
    }
  }

  onCategoryFilterChange = (e, val) => {
    if (!val || !val.key) return
    const items = filterTemplatesRows(
      this.state.masterTemplateList,
      this.state.wabaFilterSelected,
      val,
      this.state.typeFilterSelected,
      this.state.statusFilterSelected,
      this.state.searchText
    )
    this.setState({
      categoryFilterSelected: val,
      items,
    })
  }

  onTypeFilterChange = (e, val) => {
    if (!val || !val.key) return
    const items = filterTemplatesRows(
      this.state.masterTemplateList,
      this.state.wabaFilterSelected,
      this.state.categoryFilterSelected,
      val,
      this.state.statusFilterSelected,
      this.state.searchText
    )
    this.setState({
      typeFilterSelected: val,
      items,
    })
  }

  onStatusFilterChange = (e, val) => {
    if (!val || !val.key) return
    const items = filterTemplatesRows(
      this.state.masterTemplateList,
      this.state.wabaFilterSelected,
      this.state.categoryFilterSelected,
      this.state.typeFilterSelected,
      val,
      this.state.searchText
    )
    this.setState({
      statusFilterSelected: val,
      items,
    })
  }

  onSearch = (searchText = '') => {
    const items = filterTemplatesRows(
      this.state.masterTemplateList,
      this.state.wabaFilterSelected,
      this.state.categoryFilterSelected,
      this.state.typeFilterSelected,
      this.state.statusFilterSelected,
      searchText
    )
    this.setState({ searchText, items })
  }

  clearSearch = () => {
    const items = filterTemplatesRows(
      this.state.masterTemplateList,
      this.state.wabaFilterSelected,
      CATEGORY_FILTER_ALL,
      TYPE_FILTER_ALL,
      STATUS_FILTER_ALL,
      ''
    )
    this.setState({
      searchText: '',
      categoryFilterSelected: CATEGORY_FILTER_ALL,
      typeFilterSelected: TYPE_FILTER_ALL,
      statusFilterSelected: STATUS_FILTER_ALL,
      items,
    })
  }

  openPreviewDialog = (current) => {
    this.setState({
      previewTemplateComponents: normalizePreviewComponents(current.components),
      isTemplatePreviewDialogOpen: true,
      templatePreviewTitle: current.name || '—',
    })
  }

  renderAddTemplateDialog() {
    return (
      <PageDialog
        PageComponent={WhatsappTemplateAddForm}
        PageComponentProps={{
          ms: this.ms,
          orgId: this.orgId,
          waFromNumbers: this.state.waFromNumbers || [],
          waNumberDetails: this.state.waNumberDetails,
          crmNumbers: this.crmNumbers,
          selectedWabaNumber: (this.state.wabaFilterSelected && this.state.wabaFilterSelected.key) || '',
          defaultTemplateName: Helper.nextNonClashingIndexedName(
            'new_template_',
            (this.state.masterTemplateList || []).map(r => r && r.name)
          ),
          initialForm: this.state.addTemplateInitialForm,
          onSubmitted: (result = {}) => {
            this.setState({ isAddTemplateDialogOpen: false,addTemplateInitialForm: null }, () => {
              if (result.isDifferentWaba && result.wabaNumber) {
                this.showMessage(`Template copied successfully. Switch to ${buildWaNumberLabel(result.wabaNumber, this.state.waNumberDetails)} to view the template.`, undefined, 'Success')
              } else if (result.isCopy) {
                this.showMessage('Template copied successfully', undefined, 'Success')
              } else {
                this.showMessage('Template created successfully', undefined, 'Success')
              }
              this.fetchItems()
            })
          },
        }}
        title='Add Template'
        fullWidth
        fullWidthDialog={false}
        isOpen={this.state.isAddTemplateDialogOpen}
        buttonText='Create'
        onClosed={() => {
          this.setState({ isAddTemplateDialogOpen: false, addTemplateInitialForm: null })
        }}
      />
    )
  }

  renderAboveTable() {
    const wabaOptions = this.state.wabaOptions && this.state.wabaOptions.length > 0
      ? this.state.wabaOptions
      : []
    const categoryOptions = this.state.categoryOptions && this.state.categoryOptions.length > 0
      ? this.state.categoryOptions
      : [CATEGORY_FILTER_ALL]
    const typeOptions = this.state.typeOptions && this.state.typeOptions.length > 0
      ? this.state.typeOptions
      : [TYPE_FILTER_ALL]
    const statusOptions = this.state.statusOptions && this.state.statusOptions.length > 0
      ? this.state.statusOptions
      : [STATUS_FILTER_ALL]
    const wabaValue = this.state.wabaFilterSelected || wabaOptions[0] || null
    const categoryValue = this.state.categoryFilterSelected || CATEGORY_FILTER_ALL
    const typeValue = this.state.typeFilterSelected || TYPE_FILTER_ALL
    const statusValue = this.state.statusFilterSelected || STATUS_FILTER_ALL
    const multiWaba = wabaOptions.length > 1
    const multiCategory = categoryOptions.length > 1
    const multiType = typeOptions.length > 1
    const multiStatus = statusOptions.length > 1
    const showToolbarRefreshSpinner = (this.state.fetchState === ApiHelper.State.LOADING
      && this.state.loadingMode === ApiHelper.LOADING_MODE.INSTANT_LOAD)
      || this.state.wabaTemplatesLoading
    const availableKeys = this.state.availableKeys || []
    const unused = unusedKeys(availableKeys, this.state.keyAssignments)
    const canEdit = this.canEditTemplateKeys()

    return (
      <div className={css(Styles.toolbar)}>
        <div className={css(Styles.filterContainer)}>
          <div className={css(Styles.formControlContainer)}>
            <p className={css(Styles.label)}>WABA number</p>
            <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
              <Autocomplete
                value={wabaValue}
                options={wabaOptions}
                onChange={(e, val) => this.onWabaFilterChange(e, val)}
                getOptionLabel={option => option.label || option.key || ''}
                getOptionSelected={(option, v) => option.key === v.key}
                disableClearable
                forcePopupIcon={multiWaba}
                popupIcon={multiWaba ? undefined : null}
                open={multiWaba ? undefined : false}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant='standard'
                    className={css(Styles.formControlFilters)}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      readOnly: true,
                      classes: {
                        root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                        input: css(Styles.formInputStyle, Styles.textInput),
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className={css(Styles.formControlContainer)}>
            <p className={css(Styles.label)}>Category</p>
            <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
              <Autocomplete
                value={categoryValue}
                options={categoryOptions}
                onChange={(e, val) => this.onCategoryFilterChange(e, val)}
                getOptionLabel={option => option.label || option.key || ''}
                getOptionSelected={(option, v) => option.key === v.key}
                disableClearable
                forcePopupIcon={multiCategory}
                popupIcon={multiCategory ? undefined : null}
                open={multiCategory ? undefined : false}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant='standard'
                    className={css(Styles.formControlFilters)}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      readOnly: true,
                      classes: {
                        root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                        input: css(Styles.formInputStyle, Styles.textInput),
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className={css(Styles.formControlContainer)}>
            <p className={css(Styles.label)}>Type</p>
            <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
              <Autocomplete
                value={typeValue}
                options={typeOptions}
                onChange={(e, val) => this.onTypeFilterChange(e, val)}
                getOptionLabel={option => option.label || option.key || ''}
                getOptionSelected={(option, v) => option.key === v.key}
                disableClearable
                forcePopupIcon={multiType}
                popupIcon={multiType ? undefined : null}
                open={multiType ? undefined : false}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant='standard'
                    className={css(Styles.formControlFilters)}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      readOnly: true,
                      classes: {
                        root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                        input: css(Styles.formInputStyle, Styles.textInput),
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className={css(Styles.formControlContainer)}>
            <p className={css(Styles.label)}>Status</p>
            <FormControl className={css(Styles.formControlMainFilters)} variant='outlined'>
              <Autocomplete
                value={statusValue}
                options={statusOptions}
                onChange={(e, val) => this.onStatusFilterChange(e, val)}
                getOptionLabel={option => option.label || option.key || ''}
                getOptionSelected={(option, v) => option.key === v.key}
                disableClearable
                forcePopupIcon={multiStatus}
                popupIcon={multiStatus ? undefined : null}
                open={multiStatus ? undefined : false}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant='standard'
                    className={css(Styles.formControlFilters)}
                    InputProps={{
                      ...params.InputProps,
                      disableUnderline: true,
                      readOnly: true,
                      classes: {
                        root: css([Styles.textInputStyles, Styles.autoCompleteInputRoot]),
                        input: css(Styles.formInputStyle, Styles.textInput),
                      },
                    }}
                  />
                )}
              />
            </FormControl>
          </div>
          <div className={css(Styles.refreshActionContainer)}>
            <p className={css(Styles.label)}>&nbsp;</p>
            <div className={css(Styles.refreshActionSlot)}>
              {!showToolbarRefreshSpinner && (
                <Tooltip title='Refresh' placement='top-start'>
                  <IconButton
                    className={css(Styles.refreshIconButton)}
                    onClick={() => this.fetchItems()}
                    aria-label='Refresh'
                  >
                    <i
                      className='material-icons-outlined'
                      style={{ fontSize: 24, color: AppTheme.primaryColor }}
                    >
                      refresh
                    </i>
                  </IconButton>
                </Tooltip>
              )}
              {showToolbarRefreshSpinner && UiHelper.itemLoadingViewButton(24)}
            </div>
          </div>
        </div>
        <div className={css(Styles.searchBarContainer)} onSubmit={e => e.preventDefault()}>
          <SearchBar
            hint='Search by template name'
            onSearch={this.onSearch}
            onClearSearch={this.clearSearch}
            searchText={this.state.searchText}
            searchButtonLabel='Filter'
            clearSearchLabel='Clear'
            autoRefresh
            debounceDuration={1}
            autoFocus={false}
            hideFiltersContainer
          />
        </div>
        {availableKeys.length > 0 && unused.length > 0 && (
          <div className={css(Styles.unusedKeysRow, Styles.unusedTagsRow)}>
            <span className={css(Styles.unusedKeysLabel, Styles.unusedTagsLabel)}>Unused keys</span>
            <div className={css(Styles.keyChips, Styles.tagChips)}>
              {unused.map(key => {
                const isDeleting = this.state.deletingKey === key
                return (
                  <span
                    key={key}
                    className={css(
                      Styles.keyChip,
                      Styles.tagChip,
                      Styles.unusedKeyChip,
                      isDeleting && Styles.chipDeleting
                    )}
                  >
                    <span>{key}</span>
                    {canEdit && (
                      <span
                        role='button'
                        tabIndex={0}
                        aria-label={`Delete ${key}`}
                        title={`Delete ${key}`}
                        className={css(Styles.chipDeleteBtn)}
                        onClick={(e) => {
                          e.stopPropagation()
                          this.deleteKeyFromSettings(key)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation()
                            e.preventDefault()
                            this.deleteKeyFromSettings(key)
                          }
                        }}
                      >
                        <i className='material-icons-outlined' style={{ fontSize: 13, display: 'block' }}>close</i>
                      </span>
                    )}
                  </span>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  renderTemplatePreviewDialog() {
    return (
      <PageDialog
        PageComponent={WhatsappTemplatePreview}
        PageComponentProps={{ components: this.state.previewTemplateComponents || [] }}
        title={this.state.templatePreviewTitle || '—'}
        fullWidthDialog={false}
        isOpen={this.state.isTemplatePreviewDialogOpen}
        onClosed={() => {
          this.setState({
            isTemplatePreviewDialogOpen: false,
            previewTemplateComponents: [],
            templatePreviewTitle: '',
          })
        }}
      />
    )
  }

  renderBelowTable() {
    return (
      <>
        {this.state.isTemplatePreviewDialogOpen && this.renderTemplatePreviewDialog()}
        {this.state.isAddTemplateDialogOpen && this.renderAddTemplateDialog()}
        {this.state.isBulkCopyDialogOpen && this.renderBulkCopyTemplateDialog()}
      </>
    )
  }

  renderBulkCopyTemplateDialog() {
    return (
      <PageDialog
        PageComponent={BulkCopyTemplateForm}
        PageComponentProps={{
          ms: this.ms,
          orgId: this.orgId,
          selectedTemplate: this.state.bulkCopyTemplate,
          availableWabas: this.state.bulkCopyAvailableWabas || [],
          copyTemplateInitialForm: this.buildCopyTemplateInitialForm(this.state.bulkCopyTemplate),
          crmNumbers: this.crmNumbers,
          onSubmit: this.onBulkCopySubmit,
          inProgress: this.state.bulkCopyInProgress,
        }}
        title='Bulk Copy Template'
        fullWidth
        maxWidth='md'
        isOpen={this.state.isBulkCopyDialogOpen}
        buttonText='Submit'
        onClosed={this.closeBulkCopyDialog}
      />
    )
  }

  onActions = (current) => {
    const actions = []
    actions.push(
      <Tooltip title='Preview' placement='top-start' key={`${current.id}-preview`}>
        <span className={css(AppStyles.entityTableLinkIcon)}>
          <i
            className='material-icons-outlined'
            onClick={(e) => {
              e.stopPropagation()
              this.openPreviewDialog(current)
            }}
          >
            desktop_windows
          </i>
        </span>
      </Tooltip>
    )
    if (this.canEditTemplateKeys()) {
      actions.push(
        <Tooltip title='Manage keys' placement='top-start' key={`${current.id}-keys`}>
          <span className={css(AppStyles.entityTableLinkIcon)}>
            <i
              className='material-icons-outlined'
              onClick={(e) => {
                e.stopPropagation()
                this.openKeyDialog(current)
              }}
            >
              local_offer
            </i>
          </span>
        </Tooltip>
      )
    }
    return actions
  }
  onMenuItems = (current) =>{
    const actions = []
    const menuItems = [
      {
        current: current,
        key: current.id + '_copy',
        action: () => {
          this.openCopyTemplateDialog(current)
        },
        title: Helper.getString('createcopy'),
        icon: 'copy',
      },
      {
        current: current,
        key: current.id,
        action: () => {
          this.openBulkCopyTemplateDialog(current)
        },
        title: 'Bulk copy',
        icon: 'copy_all',
      }
    ]
    
    menuItems.map(item => {
      actions.push(UiHelper.actionButton(item))
    })
    return actions
  }
  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }
}

const Styles = StyleSheet.create({
  toolbar: {
    width: '100%',
    marginBottom: 16,
  },
  filterContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 16,
    overflowX: 'hidden',
    overflowY: 'visible',
    minWidth: 0,
    '@media (min-width: 1024px)': {
      flexWrap: 'nowrap',
    },
  },
  refreshActionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-end',
    gap: 5,
    flex: '0 0 auto',
    minWidth: 48,
  },
  refreshActionSlot: {
    width: 48,
    minWidth: 48,
    maxWidth: 48,
    height: 48,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  refreshIconButton: {
    padding: 8,
  },
  formControlContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: 5,
    flex: '1 1 220px',
    minWidth: 0,
    '@media (min-width: 1024px)': {
      flex: '1 1 0%',
      minWidth: 0,
    },
  },
  label: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    display: 'inline-block',
  },
  formControlMainFilters: {
    width: '100%',
    '@media (max-width: 767px)': {
      width: '100%',
    },
  },
  formControlFilters: {
    borderRadius: AppTheme.borderRadius,
    border: '1px solid #E5E5E5',
    overflow: 'hidden',
  },
  textInputStyles: {
    padding: '11px 20px',
    flex: 1,
    textAlign: 'left',
    color: '#222222',
    fontSize: 16,
  },
  autoCompleteInputRoot: {
    paddingRight: 48,
  },
  formInputStyle: {
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  textInput: {
    padding: '4px 0px',
  },
  searchBarContainer: {
    width: '100%',
    minWidth: 0,
    overflowX: 'hidden',
  },
  line1: {
    fontSize: 16,
  },
  nameCell: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 6,
    wordBreak: 'normal',
  },
  nameTitle: {
    flex: '0 0 auto',
    whiteSpace: 'nowrap',
  },
  line2: {
    fontSize: 14,
    color: 'gray',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '16px',
    padding: '4px 10px',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  statusBadgeDefault: {
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
  },
  statusBadgeSuccess: {
    backgroundColor: '#3BDE86',
    color: '#222222',
  },
  statusBadgePending: {
    backgroundColor: '#FFD7BB',
    color: '#222222',
  },
  statusBadgeError: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
  },
  keyChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
  },
  keyChip: {
    display: 'inline-block',
    flex: '0 0 auto',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    wordBreak: 'normal',
    whiteSpace: 'nowrap',
  },
  unusedKeyChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 4px 2px 8px',
  },
  chipDeleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16,
    height: 16,
    borderRadius: '50%',
    cursor: 'pointer',
    color: '#6B7280',
    backgroundColor: 'transparent',
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: '#E5E7EB',
      color: '#111827',
    },
  },
  chipDeleting: {
    opacity: 0.5,
    pointerEvents: 'none',
  },
  unusedKeysRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  unusedKeysLabel: {
    fontSize: 12,
    color: CommonTheme.subTextColor,
    margin: 0,
  },
  tagChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
  },
  tagChip: {
    display: 'inline-block',
    flex: '0 0 auto',
    backgroundColor: '#F3F4F6',
    color: '#374151',
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 500,
    wordBreak: 'normal',
    whiteSpace: 'nowrap',
  },
  unusedTagsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  unusedTagsLabel: {
    fontSize: 12,
    color: CommonTheme.subTextColor,
    margin: 0,
  },
})
